/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { BookOpen, Plus, Pencil, Trash2, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter33 P5 — Admin Dictionaries.
 * 
 * Currently manages the `offer_type_mappings` table — DB-backed normalization
 * for raw "Tip oferta" values from imports. Editor is admin-only; backoffice
 * has read-only visibility.
 */
export default function AdminDictionaries() {
  const { user } = useAuth();
  // Iter52 P14e — Backoffice has full CRUD on dictionaries (same as admin)
  const isAdmin = user?.role === "admin" || user?.role === "backoffice";
  
  // Iter34b — tab switcher between offer-type and distribution-zone dictionaries
  const [dictType, setDictType] = useState("offer-type"); // "offer-type" | "distribution-zone"
  const isOfferType = dictType === "offer-type";
  const apiPath = isOfferType ? "offer-type-mappings" : "distribution-zone-mappings";
  const dictLabel = isOfferType ? "Tip ofertă" : "Zonă distribuție";
  const dictDescription = isOfferType
    ? "Easy importer consultă acest dicționar înainte de salvarea `product_name`. Toate câmpurile sunt case-insensitive."
    : "Easy + Legacy importere consultă acest dicționar înainte de salvarea `distribution_zone`. Toate câmpurile sunt case-insensitive.";
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ raw_value: "", canonical_value: "", supplier_neutral: true, active: true });
  const [saving, setSaving] = useState(false);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (onlyActive) params.set("only_active", "true");
      const r = await axios.get(`${API}/api/admin/${apiPath}?${params.toString()}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [search, onlyActive, dictType]);
  
  const openCreate = () => {
    setEditingId(null);
    setForm({ raw_value: "", canonical_value: "", supplier_neutral: true, active: true });
    setDialogOpen(true);
  };
  
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      raw_value: item.raw_value,
      canonical_value: item.canonical_value,
      supplier_neutral: !!item.supplier_neutral,
      active: !!item.active,
    });
    setDialogOpen(true);
  };
  
  const saveMapping = async () => {
    if (!form.raw_value.trim() || !form.canonical_value.trim()) {
      toast.error("Ambele câmpuri sunt obligatorii");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const patchPayload = {
          canonical_value: form.canonical_value.trim(),
          active: form.active,
        };
        if (isOfferType) patchPayload.supplier_neutral = form.supplier_neutral;
        await axios.patch(`${API}/api/admin/${apiPath}/${editingId}`, patchPayload);
        toast.success("Mapping actualizat");
      } else {
        const postPayload = {
          raw_value: form.raw_value.trim(),
          canonical_value: form.canonical_value.trim(),
          active: form.active,
        };
        if (isOfferType) postPayload.supplier_neutral = form.supplier_neutral;
        await axios.post(`${API}/api/admin/${apiPath}`, postPayload);
        toast.success("Mapping creat");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };
  
  const deleteMapping = async (item) => {
    if (!window.confirm(`Ștergi mapping-ul "${item.raw_value}" → "${item.canonical_value}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/${apiPath}/${item.id}`);
      toast.success("Mapping șters");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };
  
  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dictionaries-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/admin/gdpr-compliance" className="hover:text-[#1e3a8a]">Administrare</Link>
            <ArrowRight className="h-3 w-3" />
            <span>Dicționare</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Dicționare
          </h1>
          <p className="mt-1 text-slate-500">
            Normalizare valori brute din import (Tip ofertă etc.) către o formă canonică.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={openCreate}
            className="bg-[#1e3a8a] hover:bg-[#1e40af]"
            data-testid="create-mapping-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Mapping nou
          </Button>
        )}
      </div>
      
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4 text-[#1e3a8a]" />
                {dictLabel} — mapări normalizare
              </CardTitle>
              <CardDescription>
                {dictDescription}
              </CardDescription>
            </div>
            {/* Iter34b — Tab switcher (offer-type vs distribution-zone) */}
            <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="dict-tabs">
              <button
                type="button"
                onClick={() => setDictType("offer-type")}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  isOfferType ? "bg-white shadow-sm text-[#1e3a8a]" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="dict-tab-offer-type"
              >
                Tip ofertă
              </button>
              <button
                type="button"
                onClick={() => setDictType("distribution-zone")}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  !isOfferType ? "bg-white shadow-sm text-[#1e3a8a]" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="dict-tab-distribution-zone"
              >
                Zonă distribuție
              </button>
            </div>
          </div>
          
          <div className="mt-3 flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Caută raw sau canonical…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                data-testid="search-input"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
                data-testid="only-active-toggle"
              />
              Doar active
            </label>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Raw value</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Canonical</th>
                  {isOfferType && (
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Neutral furnizor</th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={isOfferType ? 5 : 4} className="px-3 py-8 text-center text-slate-400">Se încarcă…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={isOfferType ? 5 : 4} className="px-3 py-8 text-center italic text-slate-400" data-testid="empty-state">
                    Niciun mapping definit. {isAdmin && "Adaugă primul folosind butonul de mai sus."}
                  </td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-slate-50/50" data-testid={`mapping-row-${it.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{it.raw_value}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{it.canonical_value}</td>
                    {isOfferType && (
                      <td className="px-3 py-2">
                        {it.supplier_neutral ? (
                          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">DA</Badge>
                        ) : (
                          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500">NU</Badge>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      {it.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">Activ</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Inactiv</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(it)}
                            className="h-7 w-7 p-0"
                            data-testid={`edit-${it.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMapping(it)}
                            className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                            data-testid={`delete-${it.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && items.length > 0 && (
            <div className="mt-3 text-xs text-slate-500" data-testid="total-count">
              {items.length} mapări
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="mapping-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editează mapping" : "Adaugă mapping nou"}
            </DialogTitle>
            <DialogDescription>
              Valoarea brută (exact ca în Excel) → valoare canonică folosită intern.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Raw value (din fișier import)</Label>
              <Input
                value={form.raw_value}
                onChange={(e) => setForm({ ...form, raw_value: e.target.value })}
                placeholder="ex. PPC Confort 12 luni"
                disabled={!!editingId}
                className="mt-1"
                data-testid="form-raw-value"
              />
              {editingId && (
                <p className="mt-1 text-[11px] text-slate-400">Raw value este imutabil după creare.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Canonical value (folosit intern)</Label>
              <Input
                value={form.canonical_value}
                onChange={(e) => setForm({ ...form, canonical_value: e.target.value })}
                placeholder="ex. Confort 12 luni"
                className="mt-1"
                data-testid="form-canonical-value"
              />
            </div>
            <div className="flex items-center gap-4">
              {isOfferType && (
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.supplier_neutral}
                    onChange={(e) => setForm({ ...form, supplier_neutral: e.target.checked })}
                    data-testid="form-supplier-neutral"
                  />
                  Neutru față de furnizor
                </label>
              )}
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="form-active"
                />
                Activ
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Anulează
            </Button>
            <Button
              onClick={saveMapping}
              disabled={saving}
              className="bg-[#1e3a8a] hover:bg-[#1e40af]"
              data-testid="form-submit"
            >
              {saving ? "Se salvează…" : (editingId ? "Salvează" : "Creează")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
